<template>
  <div>
    <div class="aboutsection-copy wf-section">
      <p class="paragraph-5">About dublab</p>
    </div>
    <div class="section-2 wf-section">
      <div class="container-4 w-container">
        <h1 id="core-values" class="values">Core Values</h1>
        <ul role="list" class="list">
          <li class="corevalue">
            Support for <strong>COMMUNITY</strong> is at the core of everything
            we do. dublab exists to serve our community of DJs, artists,
            contributors, neighbors, and local and global listeners. dublab is a
            platform and conduit for the creative expression of our community
            and a hub for our community to collaborate and cross-pollinate. As a
            nonprofit organization, we are owned by our community; we firmly
            believe that you cannot build community without giving up ownership
            of it.
          </li>
          <li class="corevalue">
            We are woven from the fabric of <strong>LOS ANGELES</strong>—the
            city and its residents inform our actions and direction as a global
            organization. dublab exists in a symbiotic relationship with our
            home city: we amplify its creative communities by providing them a
            global platform, while carrying voices home from around the world to
            help enrich our city’s sound. While our influences extend far beyond
            our city’s borders, our roots are in LA.
          </li>
          <li class="corevalue">
            We seek to build <strong>EQUITY</strong> within the dublab community
            in order to build equity in the music world beyond. We strive
            against gatekeeping by providing access, resources, information, and
            opportunities to those who societally and/or historically lack them.
            dublab is committed to providing a platform for those who
            wouldn&#x27;t otherwise have one.
          </li>
          <li class="corevalue">
            We pride ourselves in <strong>DIVERSITY</strong> of identity, taste,
            culture, and background. dublab revels in this diversity and in
            uplifting the voices of our diverse communities. We seek to create a
            home for DJs of all backgrounds who in turn create a home for
            listeners of all backgrounds. Representation matters.
          </li>
          <li class="corevalue">
            We deeply believe that <strong>MUSIC</strong> is a fundamental
            necessity for the individual and for the collective. To the
            individual, music brings solace, inspiration, healing, connection,
            strength, and expression and representation of one’s identity. To
            the collective, music and culture are critical vehicles for change.
          </li>
          <li class="corevalue">
            We ground our work in the aim of
            <strong>INDEPENDENT EXPRESSION</strong>. dublab promotes openness,
            curiosity, and expansiveness through the freedom we grant our
            communities in our programming, projects, and events. We strive to
            provide a platform for authentic and ego-free expression. We believe
            that joy, fun, and experimentation are inherently valuable to this
            goal.
          </li>
          <li class="corevalue">
            We are founded in the spirit of <strong>FUTURE ROOTS</strong> radio.
            Influential sounds of the past flow into the dynamic sounds of
            tomorrow; dublab collapses time, space, and genre to help bridge
            this continuum of music and culture. We revere the artists and
            pioneers who came before us and with a spirit of appreciation,
            generosity, and love— we pave the way for future generations.
          </li>
        </ul>
        <div class="div-block-19"></div>
      </div>
      <div class="container mission w-container">
        <div class="mission-statement">
          <div class="text-block text-block-2">Mission statement</div>
          <p class="paragraph-3">
            dublab cultivates and supports local cultural ecosystems worldwide.
            Through community-generated radio, dublab promotes curiosity,
            experimentation, inclusivity, and connection.
          </p>
        </div>
      </div>
      <div class="container-5 dei w-container" id="dei">
        <h1 id="core-values" class="values">DEI Statement</h1>
        <p class="paragraph-10">
          dublab fundamentally values equity, inclusivity, and diversity. We are
          committed to creating community-based projects that allow a mutual
          space for artists to freely and authentically express themselves
          outside the conditions of systemic oppression.
          <br /><br />Historically, broadcast media have overrepresented
          cultures of power—most typically the voices of white, cisgender
          males—to the exclusion of other communities. dublab strives to nurture
          a platform where all identities are supported and uplifted and where
          artists are given the tools they need to reach their full creative
          potential. We seek to amplify the voices of those underrepresented in
          the Los Angeles creative scene and to connect with like-minded
          environments around the globe.
        </p>
      </div>
      <div class="container-5 history w-container" id="history">
        <div class="div-block-19"></div>
        <h1 id="core-values" class="values">History</h1>
        <p class="paragraph-10">
          dublab is an independent, non-profit radio station based in Los
          Angeles. Since 1999, we have been broadcasting freeform radio covering
          a broad spectrum of genres, eras, cultures, and tastes. Over the years
          our programming has steadily expanded to include the production of
          original art exhibits, films, events record releases, and educational
          programs related to health, youth development, education, and creative
          processes.<br /><br />dublab has four affiliate stations located
          around the world. The stations operate independently from dublab US,
          but we share the same values and mission, and collaborate on various
          projects. dublab’s affiliate stations are based in Japan (Tokyo),
          Spain (Barcelona), Germany (Cologne), and Brazil (Sao Paulo).
        </p>
      </div>
      <div class="container-5 history w-container">
        <div class="div-block-19"></div>
        <h1 id="core-values" class="values">Some Facts about dublab</h1>
        <ul role="list" class="list-2">
          <li class="fact">
            The station’s first broadcast was by Aurelito &amp; Shakespeare on
            September 27, 1999.
          </li>
          <li class="fact">
            dublab was a part of the first wave of online radio stations in the
            1990s and has been the inspiration for countless others to start
            their own stations around the world and build community through
            online radio.
          </li>
          <li class="fact">
            The early years of dublab’s broadcast archives are stored in the
            Smithsonian Museum in Washington, DC, as a document of the birth of
            online radio.
          </li>
          <li class="fact">
            Although dublab’s core production is radio, we have steadily
            expanded our programming to include events, films, television,
            books, art exhibits, record releases, and other out-there projects
            that further our mission to share forward-thinking music and culture
            with an international audience.
          </li>
          <li class="fact">
            dublab&#x27;s sound logo was created by Suzanne Ciani. It is
            recorded in quadraphonic: if heard with the right decoder, the sound
            logo will pan across four speakers.
          </li>
          <li class="fact">
            dublab’s international stations operate independently from “dublab
            U.S.” Although all of our stations are linked by a common ethos,
            each station maintains its own website, identity, programming, and
            finances. dublab U.S. doesn’t benefit financially from any of our
            international affiliates.
          </li>
          <li class="fact">
            For branding purposes, dublab&#x27;s name is spelled all in
            lowercase. Occasionally we spell DUBLAB in all capitals. We only
            spell it as Dublab with a capital “D” in external applications and
            administrative forms.
          </li>
          <li class="fact">
            dublab&#x27;s main office is located at 1035 West 24<sup>th</sup>
            Street, Los Angeles, CA 90007 in the historic University Park
            neighborhood.
          </li>
        </ul>
      </div>
      <div class="w-container">
        <div class="div-block-4">
          <div class="text-block-5">Contact us</div>
          <div class="div-block-5">
            <div class="div-block-6">
              <p class="paragraph-4" id="dublabAddress">
                dublab<br />1035 West 24th Street<br />Los Angeles, CA 90007
              </p>
            </div>
            <div class="div-block-6">
              <p class="paragraph-4">
                General inquiries and promotional materials:<br />‍<br /><a
                  href="mailto:info@dublab.com"
                  >info@dublab.com</a
                >
              </p>
            </div>
            <div class="div-block-6">
              <p class="paragraph-4">
                Donations:<br /><a href="mailto:donate@dublab.com"
                  >donate@dublab.com</a
                >
                <br />‍<br />Technical Assistance:<br /><a
                  href="mailto:developer@dublab.com"
                  >developer@dublab.com</a
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AboutDetail",
};
</script>

<style scoped>
.section {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  height: 60vh;
  max-height: 800px;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  background-image: url("../assets/images/dublab_front_with_logo.jpg");
  background-position: 50% 75%;
  background-size: cover;
}

.container {
  max-width: 66%;
}

.w-container {
  margin: 0 auto;
  margin-top: 80px;
  max-width: 66%;
}

.container.mission {
  margin-top: 0px;
}

.paragraph {
  width: 70%;
  margin-right: auto;
  margin-bottom: 0px;
  margin-left: auto;
  font-family: Suisseneue, sans-serif;
  font-size: 18px;
  line-height: 25px;
}

.mission-statement {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 340px;
  margin-top: 60px;
  margin-bottom: 40px;
  border: 1px solid #000;
  border-radius: 40px;
  font-family: Adieu, sans-serif;
  font-size: 36px;
  line-height: 40px;
  font-weight: 300;
  text-align: center;
}

.text-block {
  font-family: Suisseintlmono, sans-serif;
  font-size: 16px;
  line-height: 18px;
  font-weight: 400;
  text-transform: uppercase;
}

.text-block-2 {
  margin-top: -2rem;
  font-size: 14px;
}

.paragraph-2 {
  max-width: 65%;
}

.paragraph-3 {
  width: 75%;
  margin-top: 10px;
  margin-right: auto;
  margin-left: auto;
  padding-left: 40px;
  -webkit-align-self: center;
  -ms-flex-item-align: center;
  -ms-grid-row-align: center;
  align-self: center;
  font-size: 30px;
  line-height: 38px;
  padding-left: 0px;
  font-size: 22px;
  line-height: 25px;
}

.directory-row {
  margin-bottom: 60px;
  border-top: 1px solid #000;
  font-size: 13px;
}

.text-block-3 {
  margin-top: 10px;
  margin-bottom: 10px;
  font-family: Suisseintlmono, sans-serif;
  text-transform: uppercase;
}

.img-row {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  margin-top: 0px;
  padding-top: 0px;
  -webkit-justify-content: space-around;
  -ms-flex-pack: distribute;
  justify-content: space-around;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
}

.img-holder {
  position: relative;
  height: 180px;
  margin-right: 2%;
  -webkit-align-self: auto;
  -ms-flex-item-align: auto;
  -ms-grid-row-align: auto;
  align-self: auto;
  -webkit-box-flex: 0;
  -webkit-flex: 0 auto;
  -ms-flex: 0 auto;
  flex: 0 auto;
  border-style: none;
  border-width: 1px;
  border-color: transparent;
  border-radius: 12px;
  text-align: left;
}

.div-block-2 {
  position: relative;
  width: 32%;
  height: 180px;
  -o-object-fit: none;
  object-fit: none;
}

.image {
  max-height: 100%;
  min-width: 100%;
  border-radius: 15px;
  -o-object-fit: cover;
  object-fit: cover;
}

.image-2 {
  display: inline-block;
  max-height: 100%;
  min-height: 100%;
  min-width: 100%;
  border-radius: 15px;
  -o-object-fit: cover;
  object-fit: cover;
  -o-object-position: 50% 50%;
  object-position: 50% 50%;
}

.image-3 {
  max-height: 100%;
  min-width: 100%;
  border-radius: 15px;
  -o-object-fit: cover;
  object-fit: cover;
}

.text-block-4 {
  position: absolute;
  left: 0%;
  top: 0%;
  right: auto;
  bottom: auto;
  display: inline-block;
  margin-top: 10px;
  margin-left: 15px;
  float: none;
  clear: left;
  font-family: Adieu, sans-serif;
  color: #fff;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
}

.mission-statement-copy {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  height: 340px;
  margin-top: 60px;
  margin-bottom: 40px;
  padding: 30px;
  border: 1px dashed #000;
  border-radius: 30px;
  font-family: Adieu, sans-serif;
  font-size: 36px;
  line-height: 40px;
  font-weight: 300;
  text-align: center;
}

.div-block-3 {
  width: 33%;
}

.image-4 {
  height: 100%;
  max-height: 100%;
  min-width: 100%;
}

.link-block {
  color: #000;
  text-decoration: none;
}

.div-block-4 {
  height: 180px;
  border-top: 1px solid #000;
  border-radius: 1px;
}

.section-2 {
  border: 1px none #000;
}

.text-block-5 {
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  text-align: left;
  font-family: Suisseintlmono, sans-serif;
  text-transform: uppercase;
}

.div-block-5 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  -ms-flex-pack: justify;
  justify-content: space-between;
  grid-column-gap: 5%;
  grid-row-gap: 5%;
}

.paragraph-4 {
  -webkit-align-self: center;
  -ms-flex-item-align: center;
  -ms-grid-row-align: center;
  align-self: center;
  -webkit-box-ordinal-group: 0;
  -webkit-order: -1;
  -ms-flex-order: -1;
  order: -1;
}

.div-block-6 {
  width: 30%;
  font-family: Adieu, sans-serif;
  font-size: 20px;
  line-height: 24px;
  font-weight: 300;
}

.image-5 {
  width: 100%;
  margin-top: 60px;
}

.paragraph-5 {
  font-family: Adieu, sans-serif;
  color: #fff;
  font-size: 80px;
  line-height: 70px;
  text-transform: none;
  text-shadow: 0 0 6px #000;
}

.heading {
  margin-top: 0px;
  margin-bottom: 20px;
  margin-left: auto;
  font-family: Adieu, sans-serif;
  font-size: 32px;
  line-height: 40px;
  font-weight: 300;
}

.heading.yearly {
  font-weight: 400;
}

.body {
  margin-right: auto;
  text-align: center;
}

.div-block-7 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  margin-bottom: 20px;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-align-content: flex-start;
  -ms-flex-line-pack: start;
  align-content: flex-start;
  grid-column-gap: 2.5%;
  grid-row-gap: 2.5%;
}

.div-block-8 {
  display: block;
  width: 23%;
  height: 15%;
  max-height: 15%;
  max-width: none;
  min-height: 15%;
  min-width: 150px;
  margin-right: 0%;
  margin-bottom: 40px;
  -webkit-box-ordinal-group: 2;
  -webkit-order: 1;
  -ms-flex-order: 1;
  order: 1;
  -webkit-box-flex: 0;
  -webkit-flex: 0 auto;
  -ms-flex: 0 auto;
  flex: 0 auto;
}

.div-block-9 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  width: 100%;
  height: 120%;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: start;
  -webkit-justify-content: flex-start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  -webkit-flex-wrap: nowrap;
  -ms-flex-wrap: nowrap;
  flex-wrap: nowrap;
  -webkit-box-align: baseline;
  -webkit-align-items: baseline;
  -ms-flex-align: baseline;
  align-items: baseline;
  -webkit-align-content: space-around;
  -ms-flex-line-pack: distribute;
  align-content: space-around;
}

.paragraph-6 {
  margin-top: 10px;
  font-family: Adieu, sans-serif;
  font-size: 16px;
  line-height: 20px;
  font-weight: 300;
  text-align: left;
}

.div-block-10 {
  margin-bottom: 50px;
  border-top: 1px solid #000;
}

.paragraph-7 {
  font-family: Adieu, sans-serif;
  color: #fff;
  font-size: 84px;
  line-height: 90px;
  font-weight: 300;
  text-align: center;
}

.aboutsection {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  height: 55vh;
  max-height: 800px;
  margin-bottom: -70px;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  background-image: url("../assets/images/Jeremiah-Chiu---Zoom-Room-2.jpg");
  background-position: 50% 50%;
  background-size: cover;
  background-clip: border-box;
  -webkit-text-fill-color: inherit;
}

.section-copy {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  height: 55vh;
  max-height: 800px;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  background-image: url("../assets/images/Dublab-4.jpg");
  background-position: 50% 70%;
  background-size: cover;
}

.section-copy-copy {
  position: relative;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  overflow: visible;
  height: 55vh;
  max-height: 800px;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  background-color: transparent;
  background-image: url("../assets/images/dublab_blues.png");
  background-position: 50% 50%;
  background-repeat: repeat;
  background-attachment: scroll;
  -o-object-fit: fill;
  object-fit: fill;
}

.paragraph-8 {
  margin-top: -50px;
  padding-top: 0px;
  font-family: Adieu, sans-serif;
  color: #fff;
  font-size: 80px;
  line-height: 90px;
}

.image-6 {
  background-color: transparent;
  -webkit-filter: invert(100%);
  filter: invert(100%);
}

.image-7 {
  position: absolute;
  left: auto;
  top: auto;
  right: 0%;
  bottom: 0%;
}

.image-8 {
  overflow: auto;
  -o-object-fit: cover;
  object-fit: cover;
}

.flexycontainer {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  margin-bottom: 60px;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  -ms-flex-pack: justify;
  justify-content: space-between;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-box-align: stretch;
  -webkit-align-items: stretch;
  -ms-flex-align: stretch;
  align-items: stretch;
  -webkit-align-content: space-between;
  -ms-flex-line-pack: justify;
  align-content: space-between;
  grid-auto-columns: 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 30px;
  -ms-grid-columns: 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  -ms-grid-rows: auto auto;
  grid-template-rows: auto auto;
}

.flexycontainer.rogueflex {
  width: 725.3359375px;
  margin-right: auto;
  margin-bottom: 80px;
  margin-left: auto;
  -webkit-justify-content: space-around;
  -ms-flex-pack: distribute;
  justify-content: space-around;
}

.flexycontainer.mids {
  -webkit-box-pack: start;
  -webkit-justify-content: flex-start;
  -ms-flex-pack: start;
  justify-content: flex-start;
}

.logoholder {
  position: relative;
  overflow: visible;
  width: 28%;
  margin-bottom: 20px;
  -webkit-align-self: center;
  -ms-flex-item-align: center;
  -ms-grid-row-align: center;
  align-self: center;
  -o-object-fit: fill;
  object-fit: fill;
}

.image-9 {
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100%;
  margin-bottom: 20px;
  padding-bottom: 0px;
  -o-object-fit: cover;
  object-fit: cover;
}

.image-10 {
  background-color: transparent;
  -webkit-filter: invert(100%);
  filter: invert(100%);
}

.image-11 {
  max-height: 80%;
  margin-top: 0px;
  margin-right: auto;
  margin-left: auto;
  padding-top: 0px;
  -webkit-filter: invert(100%);
  filter: invert(100%);
}

.text-block-6 {
  font-family: Suisseneue, sans-serif;
  font-size: 24px;
  line-height: 40px;
  font-weight: 300;
}

.text-block-7 {
  font-family: Suisseneue, sans-serif;
  font-size: 24px;
  line-height: 40px;
  font-weight: 300;
}

.div-block-11 {
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  -ms-flex: 1;
  flex: 1;
  font-weight: 400;
}

.div-block-12 {
  -webkit-align-self: auto;
  -ms-flex-item-align: auto;
  -ms-grid-row-align: auto;
  align-self: auto;
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  -ms-flex: 1;
  flex: 1;
}

.div-block-13 {
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  -ms-flex: 1;
  flex: 1;
}

.div-block-14 {
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  -ms-flex: 1;
  flex: 1;
}

.grid {
  display: -ms-grid;
  display: grid;
  min-width: 100%;
  grid-auto-flow: row;
  grid-auto-columns: 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  grid-template-areas: "Area";
  -ms-grid-columns: 1fr 16px 1fr;
  grid-template-columns: 1fr 1fr;
  -ms-grid-rows: auto 16px auto;
  grid-template-rows: auto auto;
}

.div-block-15 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  width: 47%;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  -ms-flex-pack: justify;
  justify-content: space-between;
  -webkit-box-flex: 0;
  -webkit-flex: 0 auto;
  -ms-flex: 0 auto;
  flex: 0 auto;
}

.div-block-15.mids {
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  -ms-flex-pack: justify;
  justify-content: space-between;
  -webkit-box-flex: 0;
  -webkit-flex: 0 auto;
  -ms-flex: 0 auto;
  flex: 0 auto;
}

.div-block-16 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  width: 46%;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-align-self: center;
  -ms-flex-item-align: center;
  align-self: center;
}

.div-block-17 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  max-width: 46%;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: start;
  -webkit-align-items: flex-start;
  -ms-flex-align: start;
  align-items: flex-start;
}

.heading-2 {
  margin-top: 0px;
  font-family: Suisseintlmono, sans-serif;
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.paragraph-9 {
  font-family: Suisseneue, sans-serif;
  font-weight: 400;
  text-align: left;
}

.div-block-18 {
  width: 4%;
}

.container-2 {
  max-width: 66%;
}

.container-3 {
  max-width: 78%;
}

.image-13 {
  -webkit-filter: invert(100%);
  filter: invert(100%);
}

.heading-3 {
  margin-top: 0px;
  margin-bottom: 20px;
  font-family: Adieu, sans-serif;
  color: #333;
  font-weight: 300;
}

.image-14 {
  min-height: 100%;
  min-width: 100%;
}

.link-block-2 {
  width: 32%;
}

.container-4 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  max-width: 66%;
  margin-top: 80px;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-flex-wrap: nowrap;
  -ms-flex-wrap: nowrap;
  flex-wrap: nowrap;
  -webkit-box-align: stretch;
  -webkit-align-items: stretch;
  -ms-flex-align: stretch;
  align-items: stretch;
}

.values {
  padding-top: 20px;
  padding-bottom: 20px;
  -webkit-align-self: center;
  -ms-flex-item-align: center;
  -ms-grid-row-align: center;
  align-self: center;
  -webkit-box-flex: 0;
  -webkit-flex: 0 auto;
  -ms-flex: 0 auto;
  flex: 0 auto;
  font-family: Adieu, sans-serif;
  font-size: 34px;
  line-height: 50px;
  font-weight: 300;
}

.list {
  display: block;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  font-family: Suisseintlmono, sans-serif;
  list-style-type: decimal;
}

.corevalue {
  list-style: decimal;
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 22px;
}

.container-5 {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
}

.container-5.dei {
  margin-bottom: 0px;
}

.paragraph-10 {
  margin-bottom: 60px;
  font-family: Suisseneue, sans-serif;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}

.div-block-19 {
  width: 100%;
  border-style: solid none none;
  border-width: 1px;
  border-color: #000;
  border-radius: 1px;
}

.list-item-2 {
  margin-bottom: 20px;
}

.fact {
  list-style: disc;
  margin-bottom: 30px;
  font-family: Suisseintlmono, sans-serif;
  font-size: 16px;
  line-height: 22px;
}

.list-2 {
  padding-top: 24px;
  padding-bottom: 53px;
  font-family: Montserrat, sans-serif;
}

.list-2.gifst {
  margin-top: -40px;
}

.image-16 {
  position: static;
  height: 100%;
  max-height: 100%;
  min-width: 100%;
  border-radius: 15px;
  -o-object-fit: cover;
  object-fit: cover;
}

.image-17 {
  max-height: 100%;
  min-width: 100%;
  border-radius: 15px;
  -o-object-fit: cover;
  object-fit: cover;
}

.majordonor {
  font-family: Suisseneue, sans-serif;
  font-size: 20px;
  line-height: 28px;
}

.toggle {
  margin-top: 40px;
  padding: 40px 20px;
  border: 2px solid #000;
  border-radius: 12px;
  font-family: Adieu, sans-serif;
  font-size: 34px;
  line-height: 44px;
  text-transform: uppercase;
}

.lilp {
  width: 80%;
  margin-top: 60px;
  margin-right: auto;
  margin-left: auto;
  font-size: 32px;
}

.keep-us-on-air {
  display: block;
  height: 220px;
  margin-top: 60px;
  margin-bottom: 40px;
  border: 1px solid #000;
  border-radius: 40px;
  font-family: Adieu, sans-serif;
  font-size: 36px;
  line-height: 40px;
  font-weight: 300;
  text-align: center;
}

.div-block-20 {
  display: block;
  width: 100%;
  height: 30vh;
  max-width: 100%;
  min-width: 100px;
}

.image-18 {
  max-height: 100%;
  min-width: 100%;
  -o-object-fit: cover;
  object-fit: cover;
}

.text-block-4-copy {
  position: absolute;
  left: 0%;
  top: 0%;
  right: auto;
  bottom: auto;
  display: inline-block;
  margin-top: 10px;
  margin-left: 15px;
  float: none;
  clear: left;
  font-family: Adieu, sans-serif;
  color: #fff;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
}

.aboutsection-copy {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  height: 55vh;
  max-height: 800px;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  background-color: rgba(41, 89, 151, 0.8);
  background-image: -webkit-gradient(
      linear,
      left top,
      left bottom,
      from(rgba(41, 107, 194, 0.7)),
      to(rgba(41, 107, 194, 0.7))
    ),
    url("../assets/images/Dublab-3.jpg");
  background-image: linear-gradient(
      180deg,
      rgba(41, 107, 194, 0.7),
      rgba(41, 107, 194, 0.7)
    ),
    url("../assets/images/Dublab-3.jpg");
  background-position: 0px 0px, 50% 50%;
  background-size: auto, cover;
  background-clip: border-box;
  -webkit-text-fill-color: inherit;
}

.heading-3-copy {
  margin-top: 0px;
  margin-bottom: 20px;
  font-family: Adieu, sans-serif;
  color: #f1d533;
  font-weight: 300;
  text-decoration: underline;
}

.heading-3-copy2 {
  margin-top: 0px;
  margin-bottom: 20px;
  font-family: Adieu, sans-serif;
  color: #979696;
  font-weight: 300;
  text-decoration: underline;
}

.heading-3-copy5 {
  margin-top: 0px;
  margin-bottom: 20px;
  font-family: Adieu, sans-serif;
  color: #9c913b;
  font-weight: 300;
  text-decoration: underline;
}

.text-block-8 {
  width: 80%;
  margin-bottom: 38px;
  -webkit-align-self: center;
  -ms-flex-item-align: center;
  -ms-grid-row-align: center;
  align-self: center;
  font-family: Adieu, sans-serif;
  font-weight: 300;
  text-align: center;
}

.img-row-copyer {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  margin-top: -46px;
  padding-top: 0px;
  -webkit-justify-content: space-around;
  -ms-flex-pack: distribute;
  justify-content: space-around;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
}

@media screen and (max-width: 991px) {
  .paragraph-3 {
    font-size: 20px;
    line-height: 24px;
  }

  .section-2 {
    line-height: 22px;
  }

  .flexycontainer.rogueflex {
    width: auto;
  }

  .div-block-15 {
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-align-self: auto;
    -ms-flex-item-align: auto;
    -ms-grid-row-align: auto;
    align-self: auto;
  }

  .div-block-15.mids {
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
  }

  .div-block-17 {
    display: none;
  }

  .image-15 {
    -webkit-box-flex: 0;
    -webkit-flex: 0 auto;
    -ms-flex: 0 auto;
    flex: 0 auto;
  }
}

@media screen and (max-width: 767px) {
  .paragraph-3 {
    padding-left: 0px;
    font-size: 18px;
    line-height: 22px;
  }

  .div-block-8 {
    min-width: 180px;
  }

  .paragraph-6 {
    font-size: 12px;
    line-height: 16px;
  }
}

@media screen and (max-width: 479px) {
  .paragraph-3 {
    font-size: 14px;
    line-height: 16px;
  }

  .heading {
    font-size: 28px;
  }

  .div-block-7 {
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
  }

  .flexycontainer {
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
  }

  .logoholder {
    width: 100%;
  }

  .div-block-15 {
    width: 100%;
  }
}

@font-face {
  font-family: "Adieu";
  src: url("../assets/fonts/Adieu-Regular.otf") format("opentype");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Suisseintlmono";
  src: url("../assets/fonts/SuisseIntlMono-Regular.otf") format("opentype");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Adieu";
  src: url("../assets/fonts/Adieu-Light.otf") format("opentype");
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Suisseneue";
  src: url("../assets/fonts/SuisseNeue-Regular.otf") format("opentype");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
</style>
